import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import PanelHeader from '@App/components/PanelHeader/PanelHeader';
import PanelList, { getPropertyRecord } from '@App/components/PanelList/PanelList';
import FlowingWaterLegendControl from '@App/components/Map/controls/FlowingWaterLegendControl/FlowingWaterLegendControl';
import {
  resetFlowingWaterGraph,
  setHoveredSegmentId,
} from '@App/components/GraphPanel/PotentialGraph/potential-graph.store';
import { formatEnergyPerYear } from '@utils/energy.utils';
import { useFlowingWaterDetails, useFlowingWaterSegments } from '@services/query.service';
import { getFeaturesBounds } from '@utils/geometry.utils';
import {
  getSegmentId,
  segmentBelongsToMunicipality,
} from '@utils/flowing-water-graph.utils';

import { getSourceColorExpression, layers, sources } from '../sources.layers';

import { inactiveSourceLayers } from './flowing-water.layers';

import './flowing-water.css';

const flowingWaterSegmentsSourceId = 'active-flowing-water-segments';
const flowingWaterSegmentsLayerId = 'active-flowing-water-segments-line';
const flowingWaterSegmentsHitAreaLayerId = 'active-flowing-water-segments-hit-area';
const hoveredFlowingWaterSegmentSourceId = 'hovered-flowing-water-segment';
const hoveredFlowingWaterSegmentBorderLayerId = 'hovered-flowing-water-segment-border';
const hoveredFlowingWaterSegmentLayerId = 'hovered-flowing-water-segment-line';
const earthRadiusKm = 6371;

function getSegmentLengthKm(start, end) {
  const [startLongitude, startLatitude] = start;
  const [endLongitude, endLatitude] = end;
  const latitudeDifference = (endLatitude - startLatitude) * (Math.PI / 180);
  const longitudeDifference = (endLongitude - startLongitude) * (Math.PI / 180);
  const startLatitudeRadians = startLatitude * (Math.PI / 180);
  const endLatitudeRadians = endLatitude * (Math.PI / 180);
  const haversine = Math.sin(latitudeDifference / 2) ** 2
    + Math.cos(startLatitudeRadians)
    * Math.cos(endLatitudeRadians)
    * Math.sin(longitudeDifference / 2) ** 2;

  return 2 * earthRadiusKm * Math.asin(Math.sqrt(haversine));
}

function getLineLengthKm(coordinates) {
  return coordinates.slice(1).reduce(
    (length, coordinate, index) => length + getSegmentLengthKm(coordinates[index], coordinate),
    0,
  );
}

function getGeometryLengthKm(geometry) {
  if (geometry?.type === 'LineString') {
    return getLineLengthKm(geometry.coordinates);
  }

  if (geometry?.type === 'MultiLineString') {
    return geometry.coordinates.reduce(
      (length, lineCoordinates) => length + getLineLengthKm(lineCoordinates),
      0,
    );
  }

  return undefined;
}

function getSegmentProperties(segment) {
  if (segment.properties) return segment.properties;

  return Object.fromEntries(
    Object.entries(segment).filter(([key]) => key !== 'geometry'),
  );
}

function getSegmentMaximums(segments) {
  const maximums = {
    'potential': 0,
    'temperature': 0,
    'heat-delivered': 0,
  };
  const propertiesList = segments.map(getSegmentProperties);

  propertiesList.forEach((properties) => {
    maximums.potential = Math.max(
      maximums.potential,
      Number(properties.Bronpotentieel_jaar_bronsleutel6) / 1000000,
    );
    maximums.temperature = Math.max(
      maximums.temperature,
      Math.abs(Number(properties.DeltaTavg_bronsleutel6)),
    );
    maximums['heat-delivered'] = Math.max(
      maximums['heat-delivered'],
      Number(properties.totale_warmte_geleverd_combosleutel_bs6_vs1) / 1000000,
    );
  });

  return maximums;
}

function getFlowingWaterDetails(data, record) {
  const lengthKm = getGeometryLengthKm(data?.geometry);
  const details = [
    ['Lengte', lengthKm === undefined ? '-' : `${lengthKm.toFixed(2)} km`],
    ['Beheerder', record.beheerder],
    ['Categorie', record.categorie],
    ['Categorie AQT', record.Categorie_AQT],
    [
      'Totale bronpotentieel op het traject',
      formatEnergyPerYear(record.Bronpotentieel_jaar_bronsleutel6),
    ],
    [
      'Resterend bronpotentieel op het traject',
      formatEnergyPerYear(record.Rest_bronpotentieel_jaar_bronsleutel6),
    ],
    ['Gemiddelde deltaT op het traject', record.DeltaTavg_bronsleutel6],
    [
      'Totale warmte geleverd op traject',
      formatEnergyPerYear(record.totale_warmte_geleverd_combosleutel_bs6_vs1),
    ],
  ];

  return details;
}

function FlowingWater({
  highlightedSegmentId,
  panelData,
  panelName,
}) {
  const { map } = useOutletContext();
  const { stromendWaterId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const legendMode = useSelector(state => state.sourceLegend.selectedMode);
  const hoveredSegmentId = useSelector(state => state.flowingWaterGraph.hoveredSegmentId);
  const municipalityFilterId = useSelector(
    state => state.flowingWaterGraph.municipalityFilterId,
  );
  const { data } = useFlowingWaterDetails(stromendWaterId);
  const { data: segments } = useFlowingWaterSegments(stromendWaterId);
  const segmentFeatures = useMemo(
    () => (Array.isArray(segments) ? segments : segments?.features || []),
    [segments],
  );
  const displayedSegmentFeatures = useMemo(
    () => (municipalityFilterId
      ? segmentFeatures.filter(
          segment => segmentBelongsToMunicipality(segment, municipalityFilterId),
        )
      : segmentFeatures),
    [segmentFeatures, municipalityFilterId],
  );
  const segmentMaximums = useMemo(
    () => getSegmentMaximums(segmentFeatures),
    [segmentFeatures],
  );
  const record = getPropertyRecord(data);
  const name = panelName || record.naam || record.Naam || record.name || 'Stromend Water';
  const details = data ? getFlowingWaterDetails(data, record) : [];

  useEffect(() => () => {
    dispatch(resetFlowingWaterGraph());
  }, [dispatch]);

  useEffect(() => {
    if (!map || !segments) return undefined;

    const featureCollection = {
      type: 'FeatureCollection',
      features: displayedSegmentFeatures.map(segment => segment.type === 'Feature'
        ? segment
        : {
            type: 'Feature',
            geometry: segment.geometry,
            properties: getSegmentProperties(segment),
          }),
    };

    if (map.getLayer(flowingWaterSegmentsLayerId)) {
      map.removeLayer(flowingWaterSegmentsLayerId);
    }
    if (map.getSource(flowingWaterSegmentsSourceId)) {
      map.removeSource(flowingWaterSegmentsSourceId);
    }

    map.addSource(flowingWaterSegmentsSourceId, {
      type: 'geojson',
      data: featureCollection,
      promoteId: 'ID_100m',
    });
    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;

    map.addLayer({
      id: flowingWaterSegmentsLayerId,
      type: 'line',
      source: flowingWaterSegmentsSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': getSourceColorExpression(
          legendMode,
          'flowingWater',
          false,
          segmentMaximums[legendMode],
        ),
        'line-width': 4,
        'line-opacity': 0.9,
      },
    }, layerAfterRoadLabelsId);
    map.addLayer({
      id: flowingWaterSegmentsHitAreaLayerId,
      type: 'line',
      source: flowingWaterSegmentsSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#000',
        'line-width': 16,
        'line-opacity': 0,
      },
    }, layerAfterRoadLabelsId);
    map.addSource(hoveredFlowingWaterSegmentSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
    map.addLayer({
      id: hoveredFlowingWaterSegmentBorderLayerId,
      type: 'line',
      source: hoveredFlowingWaterSegmentSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#fff',
        'line-width': 14,
        'line-opacity': 1,
      },
    }, layerAfterRoadLabelsId);
    map.addLayer({
      id: hoveredFlowingWaterSegmentLayerId,
      type: 'line',
      source: hoveredFlowingWaterSegmentSourceId,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': getSourceColorExpression(
          legendMode,
          'flowingWater',
          false,
          segmentMaximums[legendMode],
        ),
        'line-width': 10,
        'line-opacity': 0.9,
      },
    }, layerAfterRoadLabelsId);
    const handleMouseMove = (event) => {
      const feature = event.features?.[0];
      const featureId = feature?.id;
      if (featureId === undefined || featureId === null) return;

      dispatch(setHoveredSegmentId(String(featureId)));
      map.getCanvas().style.cursor = 'pointer';
    };
    const handleMouseLeave = () => {
      dispatch(setHoveredSegmentId(null));
      map.getCanvas().style.cursor = '';
    };
    const handleSegmentClick = (event) => {
      const featureId = event.features?.[0]?.id;
      if (featureId !== undefined && featureId !== null) {
        navigate(`/bronnen/stromend-water/${stromendWaterId}/${featureId}`);
      }
    };
    map.on('mousemove', flowingWaterSegmentsHitAreaLayerId, handleMouseMove);
    map.on('mouseleave', flowingWaterSegmentsHitAreaLayerId, handleMouseLeave);
    map.on('click', flowingWaterSegmentsHitAreaLayerId, handleSegmentClick);

    return () => {
      map.off('mousemove', flowingWaterSegmentsHitAreaLayerId, handleMouseMove);
      map.off('mouseleave', flowingWaterSegmentsHitAreaLayerId, handleMouseLeave);
      map.off('click', flowingWaterSegmentsHitAreaLayerId, handleSegmentClick);
      if (map.getLayer(flowingWaterSegmentsHitAreaLayerId)) {
        map.removeLayer(flowingWaterSegmentsHitAreaLayerId);
      }
      if (map.getLayer(hoveredFlowingWaterSegmentLayerId)) {
        map.removeLayer(hoveredFlowingWaterSegmentLayerId);
      }
      if (map.getLayer(hoveredFlowingWaterSegmentBorderLayerId)) {
        map.removeLayer(hoveredFlowingWaterSegmentBorderLayerId);
      }
      if (map.getLayer(flowingWaterSegmentsLayerId)) {
        map.removeLayer(flowingWaterSegmentsLayerId);
      }
      if (map.getSource(hoveredFlowingWaterSegmentSourceId)) {
        map.removeSource(hoveredFlowingWaterSegmentSourceId);
      }
      if (map.getSource(flowingWaterSegmentsSourceId)) {
        map.removeSource(flowingWaterSegmentsSourceId);
      }
    };
  }, [
    dispatch,
    displayedSegmentFeatures,
    highlightedSegmentId,
    legendMode,
    map,
    navigate,
    segmentMaximums,
    segments,
    stromendWaterId,
  ]);

  useEffect(() => {
    if (!map) return undefined;

    const hoveredSource = map.getSource(hoveredFlowingWaterSegmentSourceId);
    if (!hoveredSource) return undefined;

    const findFeature = targetId => displayedSegmentFeatures
      .map(segment => (segment.type === 'Feature'
        ? segment
        : {
            type: 'Feature',
            geometry: segment.geometry,
            properties: getSegmentProperties(segment),
          }))
      .find(feature => String(
        feature.id ?? feature.properties?.ID_100m ?? getSegmentId(feature),
      ) === String(targetId));

    const activeId = hoveredSegmentId ?? highlightedSegmentId;
    const feature = activeId == null ? undefined : findFeature(activeId);

    hoveredSource.setData({
      type: 'FeatureCollection',
      features: feature ? [feature] : [],
    });

    return undefined;
  }, [displayedSegmentFeatures, highlightedSegmentId, hoveredSegmentId, map, segments]);

  useEffect(() => {
    if (!map) return undefined;

    const legendControl = new FlowingWaterLegendControl(segmentMaximums);
    map.addControl(legendControl, 'bottom-right');

    return () => {
      map.removeControl(legendControl);
    };
  }, [map, segmentMaximums]);

  useEffect(() => {
    if (!map) return undefined;

    const boundsSource = municipalityFilterId && displayedSegmentFeatures.length > 0
      ? displayedSegmentFeatures
      : (data?.geometry ? [data] : undefined);
    if (!boundsSource) return undefined;

    const featureBounds = getFeaturesBounds(boundsSource);
    if (!featureBounds) return undefined;

    let secondFrame;
    const fitFeature = () => {
      map.resize();

      const [[minLongitude, minLatitude], [maxLongitude, maxLatitude]] = featureBounds;
      if (minLongitude === maxLongitude && minLatitude === maxLatitude) {
        map.flyTo({
          center: [minLongitude, minLatitude],
          zoom: 14,
          duration: 1000,
        });
        return;
      }

      map.fitBounds(featureBounds, {
        padding: {
          top: 48,
          right: 128,
          bottom: 48,
          left: 450,
        },
        maxZoom: 16,
        duration: 1000,
      });
    };
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(fitFeature);
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [data, displayedSegmentFeatures, map, municipalityFilterId]);

  useEffect(() => {
    if (!map) return undefined;

    const s3BucketUrl = import.meta.env.VITE_S3_BUCKET_URL;
    if (!s3BucketUrl) return undefined;

    const sourceEntries = Object.values(sources);
    const layerEntries = Object.values(layers);

    sourceEntries.forEach((source) => {
      if (!map.getSource(source.id)) {
        map.addSource(source.id, {
          type: source.type,
          url: `pmtiles://${s3BucketUrl}/${source.pmtilesFile}`,
          promoteId: source.promoteId,
        });
      }
    });

    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;

    layerEntries.forEach((layer) => {
      if (!map.getLayer(layer.id)) map.addLayer(layer, layerAfterRoadLabelsId);
    });

    const activeWaterId = Number(stromendWaterId);
    if (Number.isFinite(activeWaterId)) {
      const activeWaterFilter = [
        '!=',
        ['get', 'ID_stromend'],
        activeWaterId,
      ];
      map.setFilter(layers.flowingWater.id, activeWaterFilter);
      map.setFilter(layers.flowingWaterHitArea.id, activeWaterFilter);
    }

    Object.entries(inactiveSourceLayers).forEach(([layerName, paintProperties]) => {
      Object.entries(paintProperties).forEach(([property, value]) => {
        map.setPaintProperty(layers[layerName].id, property, value);
      });
    });

    const navigationTargets = {
      [layers.flowingWaterHitArea.id]: feature => `/bronnen/stromend-water/${feature.id}`,
      [layers.stillWater.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.stillWaterOutline.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.rwzi.id]: feature => `/bronnen/rwzi/${feature.id}`,
    };
    const clickHandlers = Object.entries(navigationTargets).map(([layerId, getPath]) => {
      const handler = (event) => {
        const feature = event.features?.[0];
        if (feature?.id !== undefined && feature?.id !== null) {
          navigate(getPath(feature));
        }
      };
      map.on('click', layerId, handler);
      return { layerId, handler };
    });

    const interactiveLayerIds = [
      ...Object.keys(navigationTargets),
      flowingWaterSegmentsHitAreaLayerId,
    ];
    let hoveredFeature;
    const clearHoveredFeature = () => {
      if (!hoveredFeature) return;
      map.setFeatureState(hoveredFeature, { hover: false });
      hoveredFeature = undefined;
    };
    const handleMouseMove = (event) => {
      const features = map.queryRenderedFeatures(event.point, {
        layers: interactiveLayerIds,
      });
      const feature = features[0];
      if (!feature) {
        clearHoveredFeature();
        map.getCanvas().style.cursor = '';
        return;
      }

      const featureStateTarget = {
        source: feature.source,
        sourceLayer: feature.sourceLayer,
        id: feature.id,
      };
      if (
        featureStateTarget.id !== undefined
        && featureStateTarget.id !== null
        && (
          !hoveredFeature
          || hoveredFeature.id !== featureStateTarget.id
          || hoveredFeature.source !== featureStateTarget.source
        )
      ) {
        clearHoveredFeature();
        hoveredFeature = featureStateTarget;
        map.setFeatureState(hoveredFeature, { hover: true });
      }
      map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : '';
    };
    map.on('mousemove', handleMouseMove);

    return () => {
      clickHandlers.forEach(({ layerId, handler }) => map.off('click', layerId, handler));
      map.off('mousemove', handleMouseMove);
      clearHoveredFeature();
      map.getCanvas().style.cursor = '';
      if (map.getLayer(layers.flowingWater.id)) {
        map.setFilter(layers.flowingWater.id, null);
      }
      if (map.getLayer(layers.flowingWaterHitArea.id)) {
        map.setFilter(layers.flowingWaterHitArea.id, null);
      }
      [...layerEntries].reverse().forEach(({ id }) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      [...sourceEntries].reverse().forEach(({ id }) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
  }, [map, navigate, stromendWaterId]);

  return (
    <div className="flowing-water">
      <PanelHeader
        map={map}
        rootPath="/bronnen"
        currentName={name}
      />
      {panelData ? <PanelList data={panelData} /> : <PanelList details={details} />}
    </div>
  );
}

export default FlowingWater;
